import {useSearchParams, VBox, Alert, CircularProgress} from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useEffect, useRef, useState } from 'react'

export const ExternalToolPage: FC = () => {
  const [searchParams] = useSearchParams()
  const url = searchParams.get('url')
  const name = searchParams.get('name') ?? 'External Tool'

  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    setError(null)
  }, [url])

  const handleLoad = () => {
    setLoading(false)
    // Check if the iframe actually loaded content by trying to access it
    // This will throw for cross-origin frames, which is expected and means it loaded
    try {
      const doc = iframeRef.current?.contentDocument
      // If we can access the document and it has no content, it likely failed
      if (doc && doc.body && doc.body.childElementCount === 0 && doc.title === '') {
        setError(`Could not load "${name}". The URL may be unreachable.`)
      }
    } catch {
      // Cross-origin - the page loaded successfully, we just can't inspect it
    }
  }

  const handleError = () => {
    setLoading(false)
    setError(`Could not load "${name}". The URL may be invalid or the site may not allow embedding.`)
  }

  const isSafeUrl = url ? /^https?:\/\//i.test(url) : false

  if (!url || !isSafeUrl) {
    return (
      <VBox style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <Alert severity="error">{url ? 'Invalid URL scheme. Only HTTP/HTTPS URLs are allowed.' : 'No URL provided for external tool.'}</Alert>
      </VBox>
    )
  }

  return (
    <VBox style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', paddingTop: 32, paddingBottom: 32 }}>
          <CircularProgress size={24} style={{ marginRight: 8 }} />
          Loading {name}...
        </div>
      ) : null}
      {error ? (
        <div style={{ padding: 16 }}>
          <Alert severity="error">{error}</Alert>
        </div>
      ) : null}
      <iframe
        ref={iframeRef}
        src={url}
        title={name}
        onLoad={handleLoad}
        onError={handleError}
        style={{
          flex: 1,
          border: 'none',
          width: '100%',
          height: '100%',
          display: error ? 'none' : 'block',
        }}
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
      />
    </VBox>
  )
}
